/**
 * DataTable — AG Grid Community wrapper with AW dark theme.
 *
 * Usage:
 *   <DataTable
 *     columns={[
 *       { field: 'account_code', headerName: 'Account' },
 *       { field: 'balance', headerName: 'Balance', type: 'currency' },
 *     ]}
 *     data={rows}
 *     onRowClick={(row) => navigate(`/drill/${row.account_code}`)}
 *   />
 */

import React, { useMemo, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import {
  AG_GRID_DARK_CLASS, AG_GRID_DEFAULT_COL_DEF, AG_GRID_CSS,
  formatCurrency, formatPercent, formatNumber,
} from '../../config/chartTheme';

// Register all community modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface DataTableColumn {
  field: string;
  headerName: string;
  type?: 'currency' | 'percent' | 'number' | 'date' | 'text';
  width?: number;
  flex?: number;
  sortable?: boolean;
  filter?: boolean;
  cellClass?: string;
  hide?: boolean;
}

interface DataTableProps {
  columns: DataTableColumn[];
  data: Record<string, any>[];
  height?: number | string;
  onRowClick?: (row: any) => void;
  pageSize?: number;
  loading?: boolean;
}

// Inject AG Grid CSS overrides once
let cssInjected = false;
function injectCss() {
  if (cssInjected) return;
  const style = document.createElement('style');
  style.textContent = AG_GRID_CSS;
  document.head.appendChild(style);
  cssInjected = true;
}

function currencyFormatter(params: any) {
  return formatCurrency(params.value, true);
}
function percentFormatter(params: any) {
  return formatPercent(params.value);
}
function numberFormatter(params: any) {
  return formatNumber(params.value);
}

export const DataTable: React.FC<DataTableProps> = ({
  columns, data, height = 400, onRowClick, pageSize = 50, loading,
}) => {
  useEffect(() => { injectCss(); }, []);

  const colDefs = useMemo(() => {
    return columns.map(col => {
      const def: any = {
        field: col.field,
        headerName: col.headerName,
        width: col.width,
        flex: col.flex ?? (col.width ? undefined : 1),
        sortable: col.sortable ?? true,
        filter: col.filter ?? true,
        hide: col.hide,
      };

      switch (col.type) {
        case 'currency':
          def.valueFormatter = currencyFormatter;
          def.cellClass = 'ag-cell-mono';
          def.type = 'numericColumn';
          break;
        case 'percent':
          def.valueFormatter = percentFormatter;
          def.cellClass = 'ag-cell-mono';
          def.type = 'numericColumn';
          break;
        case 'number':
          def.valueFormatter = numberFormatter;
          def.cellClass = 'ag-cell-mono';
          def.type = 'numericColumn';
          break;
        case 'date':
          def.filter = 'agDateColumnFilter';
          break;
      }

      if (col.cellClass) {
        def.cellClass = col.cellClass;
      }

      return def;
    });
  }, [columns]);

  const handleRowClicked = onRowClick
    ? (event: any) => onRowClick(event.data)
    : undefined;

  return (
    <div className={AG_GRID_DARK_CLASS} style={{ height, width: '100%' }}>
      <AgGridReact
        rowData={data}
        columnDefs={colDefs}
        defaultColDef={AG_GRID_DEFAULT_COL_DEF}
        onRowClicked={handleRowClicked}
        rowSelection="single"
        animateRows
        pagination
        paginationPageSize={pageSize}
        loading={loading}
        overlayNoRowsTemplate='<span style="color: #64748B; font-size: 13px;">No data</span>'
        overlayLoadingTemplate='<span style="color: #94A3B8; font-size: 13px;">Loading...</span>'
      />
    </div>
  );
};

export default DataTable;
